// @ts-strict-ignore
import { UpdateReviewSubmissionItemRequest, tryUpdateRelease } from '@/actions/release';
import { ReleaseUpdatable } from '@/actions/updatable';
import { AppStoreItemsCardHeader } from '@/components/AppStoreItems/AppStoreItemsCardHeader';
import { antGrey, releaseStepModuleTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { reviewItemNameForType } from '@/helpers/displayHelpers';
import {
    AppStoreReviewSubmissionItemState,
    AppStoreReviewSubmissionItemType,
    AppVersionExperiment,
    CustomProductPage,
    InAppEvent,
    InAppPurchase,
    RunwayResponseType,
} from '@/types';
import { globalMessage } from '@/utils/globalMessage';
import { ArrowRightOutlined, DeleteOutlined } from '@ant-design/icons';
import Button from 'antd/lib/button';
import Space from 'antd/lib/space';
import Text from 'antd/lib/typography/Text';
import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { Nullable, StepRunComponentProps } from '@/typescriptHelpers';
import { StepRunUpdatable } from '@/actions/stepRun';

export interface AppStoreItemsCardProps {
    children: React.ReactNode;
}

export const AppStoreItemsCard: React.FC<AppStoreItemsCardProps> = ({ children }) => {
    return (
        <div
            style={{
                backgroundColor: antGrey.grey2,
                borderRadius: 8,
                border: `1px solid ${antGrey.grey5}`,
                overflow: 'hidden',
            }}
        >
            {children}
        </div>
    );
};

export interface CommonSubmissionItemCardProps {
    reviewSubmissionItemId: string;
    state?: AppStoreReviewSubmissionItemState;
    removeButtonText?: string;
    orgId?: string;
    appId?: string;
    releaseId?: string;
    isRemoveButtonDisabled?: boolean;
    shouldHideASCLink?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
}

export interface SubmissionItemCardProps extends CommonSubmissionItemCardProps {
    type: AppStoreReviewSubmissionItemType;
    item: InAppEvent | CustomProductPage | AppVersionExperiment | InAppPurchase;
}

export const SubmissionItemCard: React.FC<React.PropsWithChildren<SubmissionItemCardProps>> = ({
    item,
    state,
    removeButtonText,
    type,
    orgId,
    appId,
    releaseId,
    children,
    isRemoveButtonDisabled,
    reviewSubmissionItemId,
    shouldHideASCLink,
    stepRunComponentProps,
}) => {
    const dispatch = useDispatch();

    const [
        removeReviewSubmissionItemStepRun,
        removeReviewSubmissionItemIsLoadingStepRun,
        removeReviewSubmissionItemResponseTypeStepRun,
        removeReviewSubmissionItemErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.RemoveReviewSubmissionItem, stepRunComponentProps);

    const removeReviewSubmissionItemIsLoading = useIsLoading(
        ReleaseUpdatable.RemoveReviewSubmissionItem,
        reviewSubmissionItemId,
    );
    const removeReviewSubmissionItemError = useError(
        ReleaseUpdatable.RemoveReviewSubmissionItem,
        reviewSubmissionItemId,
    );
    const removeReviewSubmissionItemUpdated = useUpdated(
        ReleaseUpdatable.RemoveReviewSubmissionItem,
        reviewSubmissionItemId,
    );

    const isLoading =
        stepRunComponentProps ? removeReviewSubmissionItemIsLoadingStepRun : removeReviewSubmissionItemIsLoading;
    const error = stepRunComponentProps ? removeReviewSubmissionItemErrorStepRun : removeReviewSubmissionItemError;
    const success =
        stepRunComponentProps ?
            removeReviewSubmissionItemResponseTypeStepRun === RunwayResponseType.Success
        :   removeReviewSubmissionItemUpdated;

    const removeReviewSubmissionItem = (itemId: string, category: AppStoreReviewSubmissionItemType) => {
        if (stepRunComponentProps) {
            return removeReviewSubmissionItemStepRun({ itemId });
        }

        const request: UpdateReviewSubmissionItemRequest = {
            orgId,
            appId,
            releaseId,
            itemId,
            type: category,
        };
        dispatch(tryUpdateRelease(ReleaseUpdatable.RemoveReviewSubmissionItem, request, itemId));
    };

    React.useEffect(() => {
        if (success) {
            globalMessage.success('Review submission item removed successfully!');
        }
    }, [success]);

    React.useEffect(() => {
        if (error) {
            globalMessage.error(error);
        }
    }, [error]);

    return (
        <AppStoreItemsCard>
            {state && <AppStoreItemsCardHeader state={state} />}
            <div
                style={{
                    padding: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                    }}
                >
                    <Space>
                        <Text style={{ ...releaseStepModuleTitleStyle }}>{reviewItemNameForType(type)}</Text>
                    </Space>
                    {removeButtonText && (
                        <Button
                            icon={<DeleteOutlined />}
                            danger
                            disabled={isRemoveButtonDisabled}
                            onClick={() => {
                                removeReviewSubmissionItem(reviewSubmissionItemId, type);
                            }}
                            loading={isLoading}
                        >
                            {removeButtonText}
                        </Button>
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        width: '100%',
                        justifyContent: 'space-between',
                        alignItems: 'end',
                    }}
                >
                    <div>{children}</div>

                    {!shouldHideASCLink && (
                        <Button
                            type="link"
                            icon={
                                <ArrowRightOutlined
                                    style={{
                                        color: runwayColors.primary,
                                    }}
                                />
                            }
                            target={'_blank'}
                            href={item.ascLink}
                        >
                            View in App Store Connect
                        </Button>
                    )}
                </div>
            </div>
        </AppStoreItemsCard>
    );
};
